import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { SectionBase } from '../SectionBase';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  getQuestionOptions,
  secondaryServiceSubTypeQuestionId,
} from './options';
import { Question } from '../Beneficiary/BeneficiaryLanding';
import { getActivityQuestionText } from './ActivityQuestionText';
import { GuidanceTextResponse } from '../utils';
import { useProgram } from 'shared/providers/ProgramProvider';

const marginBottom = { marginBottom: SPACERS.tiny };
const getActivitiesOptionLabel = (beneficiaryTerm?: string) => ({
  110001: (val: string) => {
    const split = val.split('. ');
    const optionLabel = split[0];
    const beneficiaryTermLowerCase =
      beneficiaryTerm?.toLowerCase() || 'beneficiaries';
    const optionText =
      optionLabel === 'Direct service' || optionLabel === 'Direct Service'
        ? `Providing goods or service directly to ${beneficiaryTermLowerCase} or their families or caregivers.`
        : optionLabel === 'Capacity development' ||
          optionLabel === 'Capacity Development'
        ? `Strengthening other organizations or programs that serve ${beneficiaryTermLowerCase}.`
        : `Changing laws or policies to benefit ${beneficiaryTermLowerCase}.`;
    return (
      <>
        <div style={marginBottom}>
          <strong>{optionLabel}</strong>
        </div>
        {optionText}
      </>
    );
  },
});

interface ActivitiesRenderProps {
  question: Question;
  programName: string;
  value: string | undefined;
  guidanceText: GuidanceTextResponse;
  serviceSubType: string | undefined;
  beneficiaryTerm: string | undefined;
  intermediaries: string | undefined;
  questionOptions: NonNullable<ReturnType<typeof getQuestionOptions>>;
  handleValueChange: (questionId: number, value: string | undefined) => unknown;
  isProgramLevelQuestion?: boolean;
}

const defaultGetLabel = (value: string) => {
  if (value.startsWith('N/A - ')) {
    const v = value.split('N/A - ');
    if (v.length === 2 && !v[0]) {
      return (
        <>
          <strong>N/A - {v[1]}</strong>
        </>
      );
    }
    return value;
  }
  return value;
};

const StyledDiv = styled.div`
  width: 100%;
`;

const ActivitiesRender: React.FC<ActivitiesRenderProps> = ({
  value,
  question,
  programName,
  guidanceText,
  questionOptions,
  beneficiaryTerm,
  intermediaries,
  serviceSubType,
  handleValueChange,
  isProgramLevelQuestion,
}) => {
  const { answers } = useProgram();
  const programAnswerText = answers?.[question.dbId]?.text;
  const seededRef = useRef(false);

  const handleChange = useCallback(
    (value: string | undefined, index?: number) => {
      if (!question) return;
      handleValueChange(question.dbId, value);
    },
    [question, handleValueChange],
  );

  // Reset the seed guard whenever the active question changes, otherwise the
  // same component instance is reused across activity questions (110001–110006)
  // and only the first one ever gets seeded from the program-level answer.
  useEffect(() => {
    seededRef.current = false;
  }, [question.dbId]);

  // One-shot auto-populate from the program-level answer when the report
  // has no answer yet. Field stays editable.
  useEffect(() => {
    if (seededRef.current) return;
    if (!isProgramLevelQuestion) return;
    if (!programAnswerText) return;
    if (value) {
      seededRef.current = true;
      return;
    }
    seededRef.current = true;
    handleValueChange(question.dbId, programAnswerText);
  }, [
    isProgramLevelQuestion,
    programAnswerText,
    value,
    question.dbId,
    handleValueChange,
  ]);

  useEffect(() => {
    if (value && !questionOptions.options.includes(value)) {
      handleChange(undefined);
    }
  }, [value, questionOptions, handleChange]);

  return (
    <SectionBase
      title="Activities"
      guidanceText={guidanceText}
      subTitle={getActivityQuestionText(
        question.dbId,
        programName,
        beneficiaryTerm,
        intermediaries,
        questionOptions.helperText,
        question.text,
      )}
      questionId={question.dbId}
      helpText={
        question.dbId === secondaryServiceSubTypeQuestionId &&
        serviceSubType && (
          <>
            <strong>Primary:</strong> {serviceSubType}
          </>
        )
      }
    >
      <StyledDiv>
        <SelectorOptions
          variant="radio"
          options={questionOptions.options}
          getLabel={
            getActivitiesOptionLabel(beneficiaryTerm)[
              question.dbId as keyof typeof getActivitiesOptionLabel
            ] || defaultGetLabel
          }
          getSelected={val => val === value}
          onClick={handleChange}
        />
      </StyledDiv>
    </SectionBase>
  );
};

export default ActivitiesRender;
